import React, { useState, useEffect } from 'react';
import { Phone, Mail, MapPin, Shield, Send, CheckCircle, AlertTriangle, Clock } from 'lucide-react';
import { COMPANY_INFO } from '../data';

export default function ContactPage() {
  const [formData, setFormData] = useState({
    navn: '',
    telefon: '',
    email: '',
    beskrivelse: '',
    website_url: '' // Honeypot field
  });

  const [errors, setErrors] = useState({});
  const [status, setStatus] = useState('idle'); // 'idle' | 'submitting' | 'success' | 'error'
  const [errorMessage, setErrorMessage] = useState('');

  useEffect(() => {
    // Set Document Metadata
    document.title = `Kontakt os | ${COMPANY_INFO.name}`;
    let metaDesc = document.querySelector('meta[name="description"]');
    if (metaDesc) {
      metaDesc.setAttribute('content', "Kontakt Vesthimmerlands Maskinskovning ApS på telefon 96 98 20 00 eller vms@vhms.dk. Få et uforpligtende tilbud på maskinskovning og naturpleje.");
    }
    let canonicalLink = document.querySelector('link[rel="canonical"]');
    if (!canonicalLink) {
      canonicalLink = document.createElement('link');
      canonicalLink.setAttribute('rel', 'canonical');
      document.head.appendChild(canonicalLink);
    }
    canonicalLink.setAttribute('href', "https://www.vesthimmerlands-maskinskovning.dk/kontakt");
  }, []);

  const validate = () => {
    const newErrors = {};
    if (!formData.navn.trim()) {
      newErrors.navn = 'Navn er påkrævet';
    }
    if (!formData.telefon.trim()) {
      newErrors.telefon = 'Telefonnummer er påkrævet';
    } else if (!/^[0-9\s+]{8,15}$/.test(formData.telefon.trim())) {
      newErrors.telefon = 'Ugyldigt telefonnummer';
    }
    if (!formData.email.trim()) {
      newErrors.email = 'E-mail er påkrævet';
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email.trim())) {
      newErrors.email = 'Ugyldig e-mailadresse';
    }
    if (!formData.beskrivelse.trim()) {
      newErrors.beskrivelse = 'Beskrivelse er påkrævet';
    }
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
    // Clear error when user starts typing
    if (errors[name]) {
      setErrors(prev => ({ ...prev, [name]: '' }));
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validate()) return;

    setStatus('submitting');
    setErrorMessage('');

    try {
      const response = await fetch('https://api.dev.auxo.dk/webform/submit', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          navn: formData.navn,
          telefon: formData.telefon,
          email: formData.email,
          beskrivelse: formData.beskrivelse,
          website_url: formData.website_url // Honeypot
        })
      });

      const result = await response.json();

      if (response.ok && result.status === 'ok') {
        setStatus('success');
        setFormData({
          navn: '',
          telefon: '',
          email: '',
          beskrivelse: '',
          website_url: ''
        });
      } else {
        setStatus('error');
        setErrorMessage(result.message || 'Der skete en fejl under afsendelsen. Prøv venligst igen.');
      }
    } catch (err) {
      console.error("Fejl ved afsendelse af formular:", err);
      setStatus('error');
      setErrorMessage('Kunne ikke oprette forbindelse til serveren. Kontroller din internetforbindelse og prøv igen.');
    }
  };

  return (
    <div className="bg-stone-950 min-h-screen">
      {/* Page Hero */}
      <section className="relative h-[35vh] min-h-[250px] flex items-center justify-center overflow-hidden bg-stone-950 border-b border-stone-900">
        <div className="absolute inset-0 z-0">
          <img 
            src="https://assets.auxodev.dk/assets/c0eeaa23-7b26-43fb-9bea-da1f57f78d16/9b0ce5d5-f7d8-4ef9-b3cb-725c910415a8.jpg" 
            alt="Kontakt os" 
            className="w-full h-full object-cover object-center opacity-20"
          />
          <div className="absolute inset-0 bg-gradient-to-t from-stone-950 via-stone-950/60 to-transparent" />
        </div>
        <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <span className="text-xs font-bold uppercase tracking-widest text-swamp-300 mb-2 block">
            Vi står klar til at hjælpe dig
          </span>
          <h1 className="text-3xl sm:text-4xl lg:text-5xl font-black text-white uppercase tracking-tight leading-none">
            Kontakt os
          </h1>
        </div>
      </section>

      {/* Main Content */}
      <section className="py-16 px-4 sm:px-6 lg:px-8 max-w-7xl mx-auto">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 items-start">
          {/* Left: Contact Info & Map (5 cols) */}
          <div className="lg:col-span-5 space-y-8">
            <div className="bg-stone-900 border border-stone-800 p-8 rounded-2xl space-y-8">
              <div>
                <span className="text-xs font-bold uppercase tracking-widest text-swamp-300">
                  Direkte kontakt
                </span>
                <h2 className="text-2xl font-black text-white uppercase tracking-tight mt-1">
                  Kontaktoplysninger
                </h2>
              </div>

              <ul className="space-y-6">
                <li className="flex items-start gap-4">
                  <div className="bg-swamp-900/90 border border-swamp-700 p-3 rounded-xl text-swamp-300 shrink-0 shadow-sm">
                    <MapPin className="w-6 h-6" />
                  </div>
                  <div>
                    <span className="block text-xs text-swamp-300 uppercase font-bold">Adresse</span>
                    <span className="block text-base font-bold text-white mt-0.5">{COMPANY_INFO.name}</span>
                    <span className="block text-sm text-stone-300 mt-0.5">{COMPANY_INFO.address}</span>
                  </div>
                </li>

                <li className="flex items-start gap-4">
                  <div className="bg-swamp-900/90 border border-swamp-700 p-3 rounded-xl text-swamp-300 shrink-0 shadow-sm">
                    <Phone className="w-6 h-6" />
                  </div>
                  <div>
                    <span className="block text-xs text-swamp-300 uppercase font-bold">Telefon</span>
                    <a 
                      href={`tel:${COMPANY_INFO.phone.replace(/\s/g, '')}`} 
                      className="block text-base font-bold text-white hover:text-swamp-300 transition-colors mt-0.5 whitespace-nowrap"
                    >
                      {COMPANY_INFO.phone}
                    </a>
                  </div>
                </li>

                <li className="flex items-start gap-4">
                  <div className="bg-swamp-900/90 border border-swamp-700 p-3 rounded-xl text-swamp-300 shrink-0 shadow-sm">
                    <Mail className="w-6 h-6" />
                  </div>
                  <div>
                    <span className="block text-xs text-swamp-300 uppercase font-bold">E-mail</span>
                    <a 
                      href={`mailto:${COMPANY_INFO.email}`} 
                      className="block text-base font-bold text-white hover:text-swamp-300 transition-colors mt-0.5 whitespace-nowrap"
                    >
                      {COMPANY_INFO.email}
                    </a>
                  </div>
                </li>

                <li className="flex items-start gap-4">
                  <div className="bg-swamp-900/90 border border-swamp-700 p-3 rounded-xl text-swamp-300 shrink-0 shadow-sm">
                    <Shield className="w-6 h-6" />
                  </div>
                  <div>
                    <span className="block text-xs text-swamp-300 uppercase font-bold">Virksomhedsinfo</span>
                    <span className="block text-sm text-stone-300 mt-0.5">CVR: {COMPANY_INFO.cvr}</span>
                    <span className="block text-xs text-swamp-300/90 mt-0.5 font-semibold">PEFC-Godkendt Skoventreprenør</span>
                  </div>
                </li>
              </ul>
            </div>

            {/* Map Container */}
            <div className="bg-stone-900 border border-stone-800 hover:border-emerald-500/50 rounded-2xl overflow-hidden relative group h-[380px] min-h-[300px] w-full shadow-lg hover:shadow-emerald-950/50 hover:shadow-2xl transition-all duration-500">
              <iframe 
                title="Kort over Vesthimmerlands Maskinskovning placering på Markvænget 7A, 9600 Aars"
                src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d2188.84711319207!2d9.5256241!3d56.8016431!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x46494467d7fc937f%3A0xafd1feebbe39aeef!2sMarkv%C3%A6nget%207A%2C%209600%20Aars!5e0!3m2!1sda!2sdk!4v1710000000000!5m2!1sda!2sdk" 
                width="100%" 
                height="100%" 
                style={{ border: 0 }} 
                allowFullScreen="" 
                loading="lazy" 
                referrerPolicy="no-referrer-when-downgrade"
                className="w-full h-full block filter grayscale-[25%] group-hover:grayscale-0 transition-all duration-500"
              ></iframe>
              <div className="absolute inset-0 pointer-events-none ring-1 ring-inset ring-white/5 group-hover:ring-emerald-500/30 rounded-2xl transition-all duration-500" />
              <div className="absolute bottom-3 right-3 z-10">
                <a 
                  href="https://www.google.com/maps/search/?api=1&query=Markv%C3%A6nget+7A,+9600+Aars" 
                  target="_blank" 
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-1.5 bg-stone-950/90 hover:bg-stone-900 text-stone-200 group-hover:text-white group-hover:border-emerald-500/50 px-3 py-1.5 rounded-lg text-xs font-bold border border-stone-700 shadow-md transition-all duration-300"
                >
                  <MapPin className="w-3.5 h-3.5 text-emerald-500 group-hover:scale-110 transition-transform" />
                  Åbn i Google Maps
                </a>
              </div>
            </div>
          </div>

          {/* Right: Contact Form (7 cols) */}
          <div className="lg:col-span-7 bg-stone-900 border border-stone-800 p-8 sm:p-10 rounded-2xl space-y-8">
            <div>
              <span className="text-xs font-bold uppercase tracking-widest text-emerald-500">
                Udfyld formularen
              </span>
              <h2 className="text-2xl font-black text-white uppercase tracking-tight mt-1">
                Send os en besked
              </h2>
              <p className="text-sm text-stone-300 mt-2">
                Udfyld formularen nedenfor med dine oplysninger og en beskrivelse af din opgave, så vender vi tilbage til dig hurtigst muligt.
              </p>
            </div>

            {status === 'success' ? (
              <div className="bg-emerald-950/40 border border-emerald-800 p-6 sm:p-8 rounded-xl text-center space-y-4 animate-in zoom-in-95 duration-200">
                <div className="w-12 h-12 bg-emerald-900 border border-emerald-700 text-emerald-400 rounded-full flex items-center justify-center mx-auto shadow-lg">
                  <CheckCircle className="w-6 h-6" />
                </div>
                <h3 className="text-lg font-bold text-white uppercase tracking-tight">Tak for din henvendelse!</h3>
                <p className="text-sm text-stone-300 max-w-md mx-auto leading-relaxed">
                  Vi har modtaget din besked og vil behandle den hurtigst muligt. Vi vender typisk tilbage inden for 24 timer på hverdage.
                </p>
                <button
                  onClick={() => setStatus('idle')}
                  className="mt-4 bg-[#348F57] hover:bg-[#2a7346] text-white px-5 py-2.5 rounded-lg text-xs font-bold uppercase tracking-wider transition-all cursor-pointer"
                >
                  Send en ny besked
                </button>
              </div>
            ) : (
              <form onSubmit={handleSubmit} className="space-y-6">
                {/* Honeypot field - ABSOLUTELY HIDDEN from humans */}
                <div className="sr-only absolute -z-50 pointer-events-none opacity-0">
                  <label htmlFor="website_url">Do not fill this field if you are human</label>
                  <input
                    type="text"
                    id="website_url"
                    name="website_url"
                    value={formData.website_url}
                    onChange={handleChange}
                    tabIndex="-1"
                    autoComplete="off"
                  />
                </div>

                {/* Name Field */}
                <div className="space-y-2">
                  <label htmlFor="navn" className="block text-xs font-bold uppercase tracking-wider text-stone-200">
                    Navn <span className="text-emerald-500">*</span>
                  </label>
                  <input
                    type="text"
                    id="navn"
                    name="navn"
                    value={formData.navn}
                    onChange={handleChange}
                    className={`w-full bg-stone-950 border ${
                      errors.navn ? 'border-red-500 focus:ring-red-500' : 'border-stone-800 focus:ring-emerald-500 focus:border-emerald-500'
                    } rounded-xl px-4 py-3.5 text-sm text-white placeholder-stone-400 focus:outline-none focus:ring-2 transition-all`}
                    placeholder="Dit fulde navn"
                  />
                  {errors.navn && <p className="text-xs text-red-500 font-semibold mt-1">{errors.navn}</p>}
                </div>

                {/* Grid for Phone and Email */}
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
                  {/* Phone Field */}
                  <div className="space-y-2">
                    <label htmlFor="telefon" className="block text-xs font-bold uppercase tracking-wider text-stone-200">
                      Telefonnummer <span className="text-emerald-500">*</span>
                    </label>
                    <input
                      type="tel"
                      id="telefon"
                      name="telefon"
                      value={formData.telefon}
                      onChange={handleChange}
                      className={`w-full bg-stone-950 border ${
                        errors.telefon ? 'border-red-500 focus:ring-red-500' : 'border-stone-800 focus:ring-emerald-500 focus:border-emerald-500'
                      } rounded-xl px-4 py-3.5 text-sm text-white placeholder-stone-400 focus:outline-none focus:ring-2 transition-all`}
                      placeholder="F.eks. 96 98 20 00"
                    />
                    {errors.telefon && <p className="text-xs text-red-500 font-semibold mt-1">{errors.telefon}</p>}
                  </div>

                  {/* Email Field */}
                  <div className="space-y-2">
                    <label htmlFor="email" className="block text-xs font-bold uppercase tracking-wider text-stone-200">
                      E-mail <span className="text-emerald-500">*</span>
                    </label>
                    <input
                      type="email"
                      id="email"
                      name="email"
                      value={formData.email}
                      onChange={handleChange}
                      className={`w-full bg-stone-950 border ${
                        errors.email ? 'border-red-500 focus:ring-red-500' : 'border-stone-800 focus:ring-emerald-500 focus:border-emerald-500'
                      } rounded-xl px-4 py-3.5 text-sm text-white placeholder-stone-400 focus:outline-none focus:ring-2 transition-all`}
                      placeholder="F.eks. vms@vhms.dk"
                    />
                    {errors.email && <p className="text-xs text-red-500 font-semibold mt-1">{errors.email}</p>}
                  </div>
                </div>

                {/* Description Field */}
                <div className="space-y-2">
                  <label htmlFor="beskrivelse" className="block text-xs font-bold uppercase tracking-wider text-stone-200">
                    Beskrivelse af opgaven <span className="text-emerald-500">*</span>
                  </label>
                  <textarea
                    id="beskrivelse"
                    name="beskrivelse"
                    value={formData.beskrivelse}
                    onChange={handleChange}
                    rows="5"
                    className={`w-full bg-stone-950 border ${
                      errors.beskrivelse ? 'border-red-500 focus:ring-red-500' : 'border-stone-800 focus:ring-emerald-500 focus:border-emerald-500'
                    } rounded-xl px-4 py-3.5 text-sm text-white placeholder-stone-400 focus:outline-none focus:ring-2 transition-all resize-none`}
                    placeholder="Beskriv opgaven, f.eks. omfang, placering, maskinkrav osv."
                  />
                  {errors.beskrivelse && <p className="text-xs text-red-500 font-semibold mt-1">{errors.beskrivelse}</p>}
                </div>

                {/* Error Banner */}
                {status === 'error' && (
                  <div className="bg-red-950/40 border border-red-800 p-4 rounded-xl flex items-start gap-3 text-red-400 text-sm animate-in fade-in duration-150">
                    <AlertTriangle className="w-5 h-5 shrink-0 mt-0.5" />
                    <p className="leading-relaxed">{errorMessage}</p>
                  </div>
                )}

                {/* Submit Button */}
                <button
                  type="submit"
                  disabled={status === 'submitting'}
                  className="w-full bg-swamp-900 hover:bg-swamp-800 disabled:bg-swamp-950 disabled:cursor-not-allowed text-white py-4 rounded-xl font-bold tracking-wide transition-all shadow-xl shadow-black/40 border border-swamp-700 hover:border-swamp-500 hover:scale-[1.01] flex items-center justify-center gap-2 text-base cursor-pointer"
                >
                  {status === 'submitting' ? (
                    <>
                      <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin" />
                      Sender besked...
                    </>
                  ) : (
                    <>
                      <span>Send besked</span>
                      <Send className="w-4 h-4 stroke-[2.5]" />
                    </>
                  )}
                </button>
              </form>
            )}
          </div>
        </div>
      </section>
    </div>
  );
}
